import { View, Text } from "@react-pdf/renderer";
import { centsToDollars, parsePrice } from "helpers/price";
import { useMemo } from "react";
import { IAbnNotice } from "types/AbnNotice/AbnNotice";

import { styles } from "../styles";

// Column widths approximate the printed CMS-R-131 grid.
const COLUMN_WIDTHS = ["41%", "38%", "21%"];

const headerCellStyle = {
  ...styles.bodyText,
  fontFamily: "Helvetica-Bold",
  fontSize: 11,
  textAlign: "center" as const,
  marginBottom: 0,
  padding: 4,
};

const bodyCellStyle = {
  ...styles.bodyText,
  fontSize: 10,
  marginBottom: 2,
};

/**
 * CLE-2659: the item/reason/cost grid of CMS-R-131 (Exp. 03/31/2029).
 *
 * The 2026 form drops the lettered column labels (D./E./F.) the previous revision used, and the
 * intro paragraph above the grid moved into MedicareDocument.
 */
export function ProceduresSection({
  activeAbnNotice,
}: {
  activeAbnNotice?: IAbnNotice | null;
}) {
  const procedures = useMemo(() => {
    return activeAbnNotice?.documentSnapshot?.procedures ?? [];
  }, [activeAbnNotice]);

  const reason = useMemo(() => {
    return (
      activeAbnNotice?.documentSnapshot?.reason ||
      "Medicare does not consider the procedure(s) listed above medically necessary for your condition or deems them experimental."
    );
  }, [activeAbnNotice]);

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: "#000",
        marginTop: 8,
        marginBottom: 8,
      }}
    >
      {/* Header row */}
      <View
        style={{
          flexDirection: "row",
          backgroundColor: "#d9d9d9",
          borderBottomWidth: 1,
          borderColor: "#000",
        }}
      >
        <Text
          style={{
            ...headerCellStyle,
            width: COLUMN_WIDTHS[0],
            borderRightWidth: 1,
            borderColor: "#000",
          }}
        >
          Item, test, service or care
        </Text>
        <Text
          style={{
            ...headerCellStyle,
            width: COLUMN_WIDTHS[1],
            borderRightWidth: 1,
            borderColor: "#000",
          }}
        >
          Reason Medicare may not pay
        </Text>
        <Text style={{ ...headerCellStyle, width: COLUMN_WIDTHS[2] }}>
          Estimated cost
        </Text>
      </View>

      {/* Body — one flex row per procedure so an item and its cost always sit on the same line
          even when the description wraps. Column-per-list layouts drift apart as soon as one
          description is longer than another, putting an amount beside the wrong service.
          The reason applies to every item, so it is printed once against the first row. */}
      {procedures.map((procedure: any, index: number) => (
        <View
          key={`${procedure.cptCode}-${index}`}
          style={{ flexDirection: "row" }}
        >
          <Text
            style={{
              ...bodyCellStyle,
              width: COLUMN_WIDTHS[0],
              borderRightWidth: 1,
              borderColor: "#000",
              padding: 4,
            }}
          >
            {procedure.cptCode} – {procedure.cptDescription}
          </Text>
          <Text
            style={{
              ...bodyCellStyle,
              width: COLUMN_WIDTHS[1],
              borderRightWidth: 1,
              borderColor: "#000",
              padding: 4,
            }}
          >
            {index === 0 ? reason : " "}
          </Text>
          <Text
            style={{
              ...bodyCellStyle,
              width: COLUMN_WIDTHS[2],
              padding: 4,
              textAlign: "right",
            }}
          >
            {parsePrice(centsToDollars(procedure.amount))}
          </Text>
        </View>
      ))}
    </View>
  );
}
